import { clsx } from 'clsx';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { useConfigurationTheme } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    Button,
    Card,
    Description,
    Heading,
    type IconDefinition,
    ProgressiveImage,
    renderIcon,
} from '@gitbook/spine-react';

import { useSetAnalyticsScreen } from '../../navigation';
import { useUpgradeDialogContext } from '../UpgradeDialog';
import { AccessWall } from './AccessWall';
import { trialAssets } from './assets';

export function TrialExpiredScreen({
    organization,
}: {
    organization: api.Organization;
}): React.ReactElement {
    const reason = 'end_trial';
    const { openUpgradeDialog } = useUpgradeDialogContext();

    useSetAnalyticsScreen(
        analytics.createOrgEvent(organization, {
            screen: 'organization_end_of_trial',
        }),
        []
    );

    return (
        <AccessWall>
            <Badge label="End of trial" kind="secondary" />
            <Heading className="mb-2 text-center text-ui-page-title">
                Make your docs your best feature
            </Heading>
            <Description className="mb-4 max-w-112 text-center">
                Create secure docs that adapt to your users, published instantly. Beautiful out of
                the box, and ready for advanced site structures.
            </Description>
            <div className="mb-10 flex gap-3">
                <Button
                    label="Upgrade"
                    kind="upgrade"
                    leadingIcon={Icon.Upgrade}
                    size="large"
                    onPress={() => {
                        openUpgradeDialog({
                            organization,
                            targetProduct: api.BillingProduct.Pro2024,
                            reason,
                        });
                    }}
                />
                <Button
                    label="Downgrade to free"
                    kind="secondary"
                    size="large"
                    onPress={() => {
                        openUpgradeDialog({
                            organization,
                            targetProduct: api.BillingProduct.Free2024,
                            reason,
                        });
                    }}
                />
            </div>
            <div className="flex h-full w-full max-w-screen-2xl flex-col gap-1 md:grid md:grid-cols-4 md:grid-rows-3 md:gap-2">
                <TrialAssetCard
                    className="col-start-1 col-end-3 row-start-1 row-end-2 lg:col-end-4"
                    title="Site sections"
                    icon={Icon.SiteSection}
                    classNameAsset="!max-w-full"
                    asset={trialAssets.siteSections}
                />
                <TrialAssetCard
                    className="col-start-3 col-end-5 row-start-1 row-end-2 lg:col-start-4"
                    title="Customize"
                    icon={Icon.Customization}
                    classNameAsset="top-[clamp(38px,30%,30%)]"
                    asset={trialAssets.customize}
                />
                <TrialAssetCard
                    className="col-start-1 col-end-3 row-start-2 row-end-3"
                    title="Authenticated access"
                    icon={Icon.PublishStatusVisitorAuth}
                    asset={trialAssets.authenticatedAccess}
                />
                <TrialAssetCard
                    className="col-start-3 col-end-5 row-start-2 row-end-3 lg:row-end-5"
                    title="AI assistant"
                    icon={Icon.AIAgent}
                    asset={trialAssets.aiAssistant}
                />
                <TrialAssetCard
                    className="col-start-1 col-end-3 row-start-3 row-end-4 lg:col-end-2"
                    title="Adaptive content"
                    icon={Icon.ReusableContent}
                    asset={trialAssets.adaptiveContent}
                />
                <TrialAssetCard
                    className="col-start-3 col-end-5 row-start-3 row-end-4 lg:col-start-2 lg:col-end-3"
                    title="Collaborate"
                    icon={Icon.Users}
                    asset={trialAssets.collaboration}
                />
            </div>
        </AccessWall>
    );
}

function TrialAssetCard({
    title,
    icon,
    asset,
    className,
    classNameAsset,
}: {
    title: string;
    icon: IconDefinition;
    asset: {
        light: string;
        dark: string;
    };
    className?: string;
    classNameAsset?: string;
}) {
    const { color: currentTheme } = useConfigurationTheme();
    const assetSrc = currentTheme === 'dark' ? asset.dark : asset.light;

    return (
        <Card
            kind="glass"
            className={clsx(
                'flex h-full min-h-40 w-full flex-col overflow-hidden p-2 md:p-4',
                className
            )}
        >
            <div className="flex items-center gap-2 text-muted">
                {renderIcon(icon, { size: 16 })}
                <span className="text-base text-ui-standout">{title}</span>
            </div>
            <div
                className={clsx(
                    '-translate-x-1/2 absolute top-[clamp(38px,22%,22%)] left-1/2 max-w-[80%]',
                    classNameAsset
                )}
            >
                <ProgressiveImage src={assetSrc} alt={title} />
            </div>
        </Card>
    );
}
